import { giphyBlack, giphyLightCharcoal, giphyLightestGrey } from '@giphy/js-brand'
import React, { useEffect, useState } from 'react'
import { fetchRelatedKeywords } from 'shared/api'
import { STATUS } from 'shared/redux/creation-tool'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled from 'styled-components'
import Placeholder from './placeholder'
import SuggestedTagsList from './suggested-tags-list'
import WhatsThis from './whats-this'

const Container = styled.div`
    width: 100%;
    background: ${giphyBlack};
    border: 1px dashed ${giphyLightCharcoal};
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 15px;
`

const TopRow = styled.div`
    width: 100%;
    height: 25%;
    position: relative;
    margin-bottom: 5px;
`

const Headline = styled.span`
    font-size: 15px;
    font-weight: bold;
    display: inline-block;
    margin: 10px;
    color: ${giphyLightestGrey};
`

const BetaPill = styled.img`
    position: relative;
    display: inline-block;
    margin-bottom: -5px;
    height: 20px;
`

const encodeGAParams = p =>
    Object.entries(p)
        .map(kv => kv.map(encodeURIComponent).join('='))
        .join('&')

type Props = {
    tags: string[]
    endpoint: string
    lastEnteredTag: string
    gifID: string | null
    addSugTag: (tag: string) => void
    status: string
    isVertical: boolean
}

const SuggestedTagWrapper = ({ tags, endpoint, lastEnteredTag, gifID, addSugTag, status, isVertical }: Props) => {
    const [keywordResponseId, setKeywordResponseId] = useState<string>('')
    const [suggestedTags, setSuggestedTags] = useState<string[]>([])
    const [isFetching, setIsFetching] = useState<boolean>(false)

    useEffect(() => {
        if (lastEnteredTag !== '') {
            fetchRelatedTags(lastEnteredTag)
        }
        if (status === STATUS.UPLOADING) {
            logUnusedSuggestedTags()
        }
    }, [lastEnteredTag, status])

    const addSuggestedTag = (tag: string) => {
        addSugTag(tag)
        removeSuggestedTag(tag, true)
        googleAnalyticsEvent({
            category: endpoint,
            action: 'Tag Accepted',
            label: encodeGAParams({
                gif_id: gifID,
                tag_name: tag,
                tag_source: 'related',
                response_id: keywordResponseId,
            }),
        })
    }

    const removeSuggestedTag = (tag: string, wasAdded = false) => {
        const tagIndex = suggestedTags.indexOf(tag)
        if (tagIndex > -1) {
            const newSuggestedTags = [...suggestedTags]
            newSuggestedTags.splice(tagIndex, 1)
            setSuggestedTags(newSuggestedTags)
        }
        if (!wasAdded) {
            googleAnalyticsEvent({
                category: endpoint,
                action: 'Tag Rejected',
                label: encodeGAParams({
                    gif_id: gifID,
                    tag_name: tag,
                    tag_source: 'related',
                    response_id: keywordResponseId,
                }),
            })
        }
    }

    const logUnusedSuggestedTags = () => {
        suggestedTags.map(tag =>
            googleAnalyticsEvent({
                category: endpoint,
                action: 'Tag Seen',
                label: encodeGAParams({
                    gif_id: gifID,
                    tag_name: tag,
                    tag_source: 'related',
                    response_id: keywordResponseId,
                }),
            })
        )
    }

    const fetchRelatedTags = async (tag: string) => {
        logUnusedSuggestedTags()
        setIsFetching(true)
        try {
            const {
                data = [],
                meta: { response_id = '' },
            } = await fetchRelatedKeywords(tag)
            const sugTags = data.map(tag => tag.name)

            setIsFetching(false)
            setSuggestedTags(sugTags)
            setKeywordResponseId(response_id)
        } catch (error) {
            setIsFetching(false)
        }
    }

    return (
        <Container>
            <TopRow>
                <Headline>Suggested Tags</Headline>
                {!isVertical && <BetaPill src="/static/img/svg/pill-beta-gradient.svg" alt="" />}
                <WhatsThis isVertical={isVertical} />
            </TopRow>
            {tags.length < 1 || isFetching ? (
                <Placeholder isFetching={isFetching} />
            ) : (
                <SuggestedTagsList
                    suggestedTags={suggestedTags}
                    onAdd={addSuggestedTag}
                    onDelete={removeSuggestedTag}
                    isVertical={isVertical}
                />
            )}
        </Container>
    )
}

export default SuggestedTagWrapper
